// ---------------------------------------------------------------------------
// Projects Data, sorted by priorityIndex (ascending). Lower = shown first.
//
// Priority is set by relevance to current career targets (semiconductor /
// hardware design / open silicon / fabrication). Position (left/right) is
// auto-alternated based on sorted index in the renderer; do not set here.
//
// To reorder, change priorityIndex. Steps of 10 leave room for inserts.
// ---------------------------------------------------------------------------

// Animated "coming soon" placeholder (rotating-diamond GIF). Used for any
// entry whose final hero image isn't ready yet. Replace src/assets/projects/
// coming-soon.gif to retheme universally, or override per-entry by setting
// `image` to a real asset.
import placeholder from '../assets/projects/coming-soon.gif';

// Robotic hand auto-tensioning loop (RDS25 capstone)
import roboticHandGif from '../assets/projects/robotic-hand.gif';

// ME382 microfluidics PIV, particle image velocimetry capture
import me382Gif from '../assets/projects/ME382.gif';

// Frogger card (gameplay screenshot + scrapbook overlay of TT layout)
import froggerCard from '../assets/projects/frogger.png';
import froggerTtOverlay from '../assets/projects/tt_frogger.png';

// ME382, micro/nano fabrication lab images (Lab 1 Pt 1 SU-8, Lab 1 Pt 2 PDMS)
import me382WaferFinished from '../assets/projects/me382_00_wafer_finished.jpg';
import me382PlasmaCleaner from '../assets/projects/me382_01_plasma_cleaner.jpg';
import me382Su8Dispense from '../assets/projects/me382_02_su8_dispense.jpg';
import me382SpinCoater from '../assets/projects/me382_03_spin_coater.jpg';
import me382Hotplate from '../assets/projects/me382_04_hotplate.jpg';
import me382UvExposure from '../assets/projects/me382_05_uv_exposure.jpg';
import me382DevelopedWafer from '../assets/projects/me382_06_developed_wafer.jpg';
import me382WaferCloseup from '../assets/projects/me382_07_wafer_closeup.jpg';
import me382Mold100 from '../assets/projects/me382_08_mold_100um.jpg';
import me382Mold50 from '../assets/projects/me382_09_mold_50um.jpg';
import me382Mold40 from '../assets/projects/me382_10_mold_40um.jpg';
import me382MoldSerpentine from '../assets/projects/me382_11_mold_serpentine.jpg';
import me382Mold20 from '../assets/projects/me382_12_mold_20um.jpg';
import me382PdmsCover from '../assets/projects/me382_20_pdms_cover.jpg';
import me382SylgardDispense from '../assets/projects/me382_21_sylgard_dispense.jpg';
import me382PdmsMixing from '../assets/projects/me382_22_pdms_mixing.jpg';
import me382PdmsCasting from '../assets/projects/me382_23_pdms_casting.jpg';
import me382DegasFoam from '../assets/projects/me382_24_degas_foam.jpg';
import me382DegasClear from '../assets/projects/me382_25_degas_clear.jpg';
import me382OvenCure from '../assets/projects/me382_26_oven_cure.jpg';
import me382PdmsDemolded from '../assets/projects/me382_27_pdms_demolded.jpg';
import me382Profilometer from '../assets/projects/me382_28_profilometer.png';

// Banner images
import camBanner from '../assets/projects/CAM_report_teaser.png';
import ce391Banner from '../assets/projects/CE391_Lab3_bitcell_bank.png';
import cnnBanner from '../assets/projects/CNN_pptx_image35.png';
import mantisBanner from '../assets/projects/MANTIS_pptx_image14.png';
import rdsBanner from '../assets/projects/RDS_IMG_4307.jpg';
import smartwatchImg from '../assets/projects/EE327_smartwatch.jpg';

// Publications images (re-used for engineering-focused project entries)
import loamFigure from '../assets/publications/LOAM1_firstfigure.png';
import loamAssembly from '../assets/publications/LOAM1_assembly.png';
import mantisPoster from '../assets/publications/MANTIS_poster.png';

// TinyTapeout images
import ttWorkshopGroup from '../assets/tinytapeout/workshop_group.jpg';
import ttWorkshopLecture from '../assets/tinytapeout/workshop_lecture.jpg';
import ttWorkshopClassroom from '../assets/tinytapeout/workshop_classroom.jpg';
import ttVgaPlayground from '../assets/tinytapeout/vga-playground.png';
import ttFlyer from '../assets/tinytapeout/flyer.png';
import ttCounterSchematic from '../assets/tinytapeout/counter_schematic.png';
import ttMpwWafer from '../assets/tinytapeout/mpw_wafer.jpg';
import ttNanofab from '../assets/tinytapeout/nanofab.jpg';

// CAM gallery
import camG1 from '../assets/projects/CAM_final_histogram_connecting_rod.png';
import camG2 from '../assets/projects/CAM_final_histogram_crankshaft.png';
import camG3 from '../assets/projects/CAM_final_histogram_piston.png';
import camG4 from '../assets/projects/CAM_final_run_chart_connecting_rod.png';
import camG5 from '../assets/projects/CAM_final_run_chart_crankshaft.png';
import camG6 from '../assets/projects/CAM_final_run_chart_piston.png';
import camG7 from '../assets/projects/CAM_report_Highlighted_Design_Element_1.png';
import camG8 from '../assets/projects/CAM_report_Highlighted_Design_Element_2.png';
import camG9 from '../assets/projects/CAM_report_Highlighted_Design_Element_3.png';
import camG10 from '../assets/projects/CAM_report_Highlighted_Design_Element_4.png';
import camG11 from '../assets/projects/CAM_report_all_crankshafts.png';
import camG12 from '../assets/projects/CAM_report_all_rods_pistons.png';
import camG13 from '../assets/projects/CAM_report_cavity_mold.png';
import camG14 from '../assets/projects/CAM_report_core_mold_drawing.png';
import camG15 from '../assets/projects/CAM_report_crankshaft_drawing.png';
import camG16 from '../assets/projects/CAM_report_final_cad_crankshaft.png';
import camG17 from '../assets/projects/CAM_report_final_cad_piston.png';
import camG18 from '../assets/projects/CAM_report_final_cad_rod.png';
import camG19 from '../assets/projects/CAM_report_manufacturing_observations.png';
import camG20 from '../assets/projects/CAM_report_milled_mold.png';
import camG21 from '../assets/projects/CAM_report_mold_drawing.png';
import camG22 from '../assets/projects/CAM_report_part_drawing.png';
import camG23 from '../assets/projects/CAM_report_piston_drawing.png';
import camG24 from '../assets/projects/CAM_report_rod_drawing.png';
import camG25 from '../assets/projects/CAM_report_subassembly.png';
import camG26 from '../assets/projects/CAM_stats_fig1_histograms.png';

// CE391 gallery
import ce391G1 from '../assets/projects/CE391_Lab1_Inverter_Sch.png';
import ce391G2 from '../assets/projects/CE391_Lab1_Layout_2.png';
import ce391G3 from '../assets/projects/CE391_Lab1_Testbench_Sch.png';
import ce391G4 from '../assets/projects/CE391_Lab3_6Tschematic.png';
import ce391G5 from '../assets/projects/CE391_Lab3_Prechargeschematic.png';
import ce391G6 from '../assets/projects/CE391_Lab3_6tlayout.png';
import ce391G7 from '../assets/projects/CE391_Lab3_csaschematic.png';

// CNN gallery
import cnnG1 from '../assets/projects/CNN_pptx_image1.png';
import cnnG2 from '../assets/projects/CNN_pptx_image10.png';
import cnnG3 from '../assets/projects/CNN_pptx_image11.png';
import cnnG4 from '../assets/projects/CNN_pptx_image12.png';
import cnnG5 from '../assets/projects/CNN_pptx_image13.png';
import cnnG6 from '../assets/projects/CNN_pptx_image14.png';
import cnnG7 from '../assets/projects/CNN_pptx_image15.png';
import cnnG8 from '../assets/projects/CNN_pptx_image16.png';
import cnnG9 from '../assets/projects/CNN_pptx_image17.png';
import cnnG10 from '../assets/projects/CNN_pptx_image18.png';
import cnnG11 from '../assets/projects/CNN_pptx_image19.png';
import cnnG12 from '../assets/projects/CNN_pptx_image2.png';
import cnnG13 from '../assets/projects/CNN_pptx_image21.png';
import cnnG14 from '../assets/projects/CNN_pptx_image22.png';
import cnnG15 from '../assets/projects/CNN_pptx_image23.png';
import cnnG16 from '../assets/projects/CNN_pptx_image24.png';
import cnnG17 from '../assets/projects/CNN_pptx_image25.png';
import cnnG18 from '../assets/projects/CNN_pptx_image26.png';
import cnnG19 from '../assets/projects/CNN_pptx_image27.png';
import cnnG20 from '../assets/projects/CNN_pptx_image28.png';
import cnnG21 from '../assets/projects/CNN_pptx_image29.png';
import cnnG22 from '../assets/projects/CNN_pptx_image3.png';
import cnnG23 from '../assets/projects/CNN_pptx_image30.png';
import cnnG24 from '../assets/projects/CNN_pptx_image31.png';
import cnnG25 from '../assets/projects/CNN_pptx_image32.png';
import cnnG26 from '../assets/projects/CNN_pptx_image33.png';
import cnnG27 from '../assets/projects/CNN_pptx_image34.png';
import cnnG28 from '../assets/projects/CNN_pptx_image36.png';
import cnnG29 from '../assets/projects/CNN_pptx_image4.png';
import cnnG30 from '../assets/projects/CNN_pptx_image5.png';
import cnnG31 from '../assets/projects/CNN_pptx_image7.png';
import cnnG32 from '../assets/projects/CNN_pptx_image8.png';
import cnnG33 from '../assets/projects/CNN_pptx_image9.png';

// Mantis Shrimp gallery (re-used for the CS353 entry)
import mantisG1 from '../assets/projects/MANTIS_pptx_image20.jpeg';
import mantisG2 from '../assets/projects/MANTIS_pptx_image21.png';
import mantisG3 from '../assets/projects/MANTIS_pptx_image26.jpeg';
import mantisG4 from '../assets/projects/MANTIS_pptx_image27.png';
import mantisG5 from '../assets/projects/MANTIS_pptx_image28.jpeg';
import mantisG6 from '../assets/projects/MANTIS_pptx_image29.png';
import mantisG7 from '../assets/projects/MANTIS_pptx_image32.png';
import mantisG8 from '../assets/projects/MANTIS_pptx_image33.png';
import mantisG9 from '../assets/projects/MANTIS_pptx_image34.png';
import mantisG10 from '../assets/projects/MANTIS_pptx_image37.png';
import mantisG11 from '../assets/projects/MANTIS_pptx_image39.jpeg';
import mantisG12 from '../assets/projects/MANTIS_sim_me-BEE-d20-h5-r0.8-p0.8.png';

// RDS gallery
import rdsG1 from '../assets/projects/RDS_20250606_143228.jpg';
import rdsG2 from '../assets/projects/RDS_GUI.png';
import rdsG3 from '../assets/projects/RDS_IMG_5379.jpg';
import rdsG4 from '../assets/projects/RDS_IMG_5380.jpg';
import rdsMotorDriver from '../assets/projects/RDS_motor_driver.jpg';
import rdsMotorDriver3d from '../assets/projects/RDS_motor_driver_3d.png';
import rdsEncoder from '../assets/projects/RDS_encoder.png';
import rdsPalmAnnotated from '../assets/projects/RDS_palm_board_annotated.png';
import rdsControlArch from '../assets/projects/RDS_control_architecture.png';
import rdsSystemDiagram from '../assets/projects/RDS_system_diagram.png';

// Bill Splitting (Figma mockups)
import billHome from '../assets/projects/bill_Home_Page.png';
import billTable from '../assets/projects/bill_Table_Page.png';
import billReceipt from '../assets/projects/bill_Table_Reciept_Modal.png';
import billJoin from '../assets/projects/bill_Join_Page.png';
import billInvite from '../assets/projects/bill_Table_Invite_Modal.png';
import billApproval from '../assets/projects/bill_Splitter_Approval_Page.png';
import billPalette from '../assets/projects/bill_Color_Palette.png';

// ARES images
import aresPolygon from '../assets/projects/ARES_polygon.png';
import aresWorkflow from '../assets/projects/ARES_workflow.png';
import aresRaster from '../assets/projects/ARES_raster.png';
import aresClustering from '../assets/projects/ARES_clustering.png';
import aresCluster1 from '../assets/projects/ARES_cluster1.png';
import aresCluster2 from '../assets/projects/ARES_cluster2.png';
import aresUtilization from '../assets/projects/ARES_utilization.png';

// NFC images
import nfcSchematicSim from '../assets/projects/NFC_schematic_sim.png';
import nfcTransient from '../assets/projects/NFC_transient.png';
import nfcDigitalTiming from '../assets/projects/NFC_digital_timing.png';
import nfcAnalogWaveforms from '../assets/projects/NFC_analog_waveforms.png';
import nfcOutputSignals from '../assets/projects/NFC_output_signals.png';
import nfcBlockDiagram from '../assets/projects/NFC_block_diagram.png';
import nfcLayout from '../assets/projects/NFC_layout.png';
import nfcSim2 from '../assets/projects/NFC_sim2.png';
import nfcSim3 from '../assets/projects/NFC_sim3.png';
import nfcSim4 from '../assets/projects/NFC_sim4.png';
import nfcSim5 from '../assets/projects/NFC_sim5.png';

// PDFs
const cnnPresentationPdf = require('../assets/projects/cnn_presentation.pdf');
const cnnReportPdf = require('../assets/projects/cnn_report.pdf');
const sramReportPdf = require('../assets/projects/sram_report.pdf');
const mantisPresentationPdf = require('../assets/projects/mantis_presentation.pdf');
const aresReportPdf = require('../assets/projects/ares_report.pdf');
const rdsMotorDriverDoc = require('../assets/projects/rds_motor_driver_doc.pdf');
const rdsMotorDriverSchematic = require('../assets/projects/rds_motor_driver_schematic.pdf');
const me382Lab1Part1Pdf = require('../assets/projects/me382_lab1_part1_report.pdf');
const me382Lab1Part2Pdf = require('../assets/projects/me382_lab1_part2_report.pdf');

const projectsData = [
  // -------------------------------------------------------------------------
  // FRONT AND CENTER, in-progress micro/nano fabrication work
  // -------------------------------------------------------------------------

  {
    id: 39,
    priorityIndex: 5,
    title: 'ME382: Experiments in Micro/Nano Fabrication',
    description:
      'Hands-on micro/nano fabrication work in the Northwestern NUFAB cleanroom. This project-based graduate course walks through the full flow for building a PDMS microfluidic device: photolithography to pattern a master mold on a silicon wafer, soft lithography to cast and demold the PDMS replica, stylus profilometry to verify the resulting feature heights and channel widths, contact-angle characterization and oxygen plasma bonding to seal the device, fluorescence-based particle image velocimetry to map the flow inside it, and atomic force microscopy to characterize the surface roughness and the elastic modulus of the cured PDMS. The same skills carry directly over to semiconductor fabrication at wafer scale.',
    course: 'MECH_ENG 382, Spring 2026',
    image: me382Gif,
    imageContain: true,
    imageBgWhite: false,
    tag: ['All', 'Mechanical Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'ME382 is Northwestern\'s graduate hands-on course in micro/nano science and engineering, taught by Profs. Nasir Basit and Horacio Espinosa. The lab work runs in the NUFAB cleanroom on a full photolithography toolset that includes the Samco PC300 plasma cleaner, the Laurell spin coater, the Suss MA/BA6 mask aligner, the Osiris automatic developer, and the Dektak stylus profilometer, then moves to a VCA goniometer for contact-angle work, a fluorescence microscope for microPIV, and a Bruker DimensionEdge AFM for both tapping-mode imaging and point force spectroscopy. Over the term the course walks an SU-8 and PDMS microfluidics flow all the way from a blank silicon wafer to a sealed device under flow and onto an AFM stage, and this page tracks the work as the term progresses.',
      tech: [
        'SU-8 3025 negative photoresist',
        'Suss MA/BA6 mask aligner',
        'Idonus UV-LED (365 nm)',
        'Laurell spin coater',
        'Samco PC300 plasma cleaner',
        'Osiris automatic developer',
        'Sylgard 184 PDMS',
        'Vacuum desiccator',
        'Dektak stylus profilometer',
        'Optical lithography microscopy',
        'VCA sessile-drop goniometer',
        'O₂ plasma surface activation',
        'Nile-Red fluorescent tracer beads',
        'Fluorescence microscope with dichroic filter cube',
        'NIS-Elements and Andor SOLIS imaging',
        'PIVview2C cross-correlation',
        'Bruker DimensionEdge AFM',
        'SCANASYST-AIR cantilever',
        'Tapping-mode AFM (AM-AFM)',
        'Structure-function fractal analysis',
        'Hertzian contact model',
        'Inkscape px → µm calibration',
        'LaTeX',
      ],
      highlights: [
        'Lab 1 Part 1 took the full SU-8 photolithography flow through to a patterned master mold on a 4-inch silicon wafer.',
        'UV exposure on the Suss MA/BA6 was calibrated to 850 mJ/cm² in soft-contact mode at a 70 µm alignment gap.',
        'Measured channel widths held within 0.6 to 13 percent of nominal, with Mold 4\'s 50 µm channel coming in at 50.8 µm (a 1.6 percent overshoot).',
        'In Lab 1 Part 2 a 10:1 Sylgard 184 PDMS replica was cast over the master, vacuum-degassed, cured at 90 °C, and demolded cleanly.',
        'Dektak stylus profilometry measured a film thickness of 20.7 µm across a 400 µm sweep, 17 percent under the 25 µm nominal for this spin recipe.',
        'PDMS channel widths track the master to within 0.6 and 2.8 percent on the well-resolved features.',
        'Lab 2 Part 1 measured the contact angle of water on PDMS at three spots before and after an O₂ plasma dose on the VCA sessile-drop goniometer, watching the surface flip from strongly hydrophobic to nearly fully wetting.',
        'Plasma-activated PDMS and glass were pressed together and baked at 90 °C for 15 min, sealing the microchannels through silanol condensation into Si-O-Si bonds.',
        'Lab 2 Part 2 ran fluorescence microPIV through the bonded device with 530 nm Nile-Red beads at syringe-pump flow rates from 0.1 to 1 µL/min, then cross-correlated paired frames in PIVview2C to recover the velocity field.',
        'Lab 3a imaged the surface in AFM tapping mode and extracted the fractal dimension D = 3 - H by fitting the log-log slope of the structure function across multiple scan sizes.',
        'Lab 3b ran AFM point force spectroscopy: the photodiode sensitivity was calibrated against a fused-quartz standard, then F-z curves on PDMS were fit with a Hertzian model to recover the Young\'s modulus.',
      ],
      images: [
        me382WaferFinished,
        me382PlasmaCleaner,
        me382Su8Dispense,
        me382SpinCoater,
        me382Hotplate,
        me382UvExposure,
        me382DevelopedWafer,
        me382WaferCloseup,
        me382Mold100,
        me382Mold50,
        me382Mold40,
        me382MoldSerpentine,
        me382Mold20,
        me382PdmsCover,
        me382SylgardDispense,
        me382PdmsMixing,
        me382PdmsCasting,
        me382DegasFoam,
        me382DegasClear,
        me382OvenCure,
        me382PdmsDemolded,
        me382Profilometer,
      ],
      story: [
        {
          heading: 'Lab 1 Part 1, SU-8 Photolithography',
          text: 'The full lithography recipe ran on the NUFAB toolset: O₂ plasma surface activation in the Samco PC300 (100 sccm, 100 W, 1 min) for SU-8 adhesion, ~4 mL dispense of SU-8 3025 on a 4-inch wafer, Laurell spin recipe (500 rpm spread / 3000 rpm dry spin → ~25 µm target film thickness), two-step bake on the Fisher Isotemp plates (65 °C / 1 min then 95 °C / 7 min), and finally UV exposure on the Suss MA/BA6 mask aligner with the chromium-on-glass ME382 microfluidic mask.',
          image: me382PlasmaCleaner,
          caption: 'Samco PC300 plasma cleaner, surface activation pass before SU-8 spin coat',
        },
        {
          heading: 'SU-8 Spin Coat',
          text: 'About 4 mL of SU-8 3025 dispensed in a puddle ~⅓ the wafer diameter, then loaded onto the Laurell chuck. Recipe: 500 rpm spread (5 s, 100 rpm/s accel, 5–10 s hold) followed by 3000 rpm dry spin (300 rpm/s accel, 40 s hold). Datasheet target at this recipe is ~25 µm film thickness.',
          image: me382Su8Dispense,
          caption: 'SU-8 3025 dispense onto a 4-inch silicon wafer, the puddle target is roughly one-third the wafer diameter',
        },
        {
          heading: 'UV Exposure on the Suss MA/BA6',
          text: 'Exposure ran in Soft-Contact / Continuous mode through the chromium-on-glass microfluidic mask, with the Idonus UV-LED engine driving 20.0 mW/cm² and an accumulated dose of 850 mJ/cm² at the wafer. Stage position read X = +23.50 / Y = −3.13 after alignment, with a 70 µm TSA alignment gap. SU-8 is a negative resist, so the exposed regions crosslink and stay; the rest dissolves in the developer.',
          image: me382UvExposure,
          caption: 'UV exposure on the Suss MA/BA6, blue LED visible through the aperture mid-exposure',
        },
        {
          heading: 'Develop and Inspect',
          text: 'Post-exposure bake (65 °C / 1 min, 95 °C / 7 min) was followed by automated development on the Osiris: 3 min SU-8 developer, spinout, repeat, then 3 min IPA twice, with 256 gentle oscillations per soak to refresh solvent at the surface. The micro-PIV spirals were visible on the developed wafer with the naked eye, with cleanly defined channel walls under the optical microscope.',
          image: me382DevelopedWafer,
          caption: 'Developed wafer, patterned spirals and straight-channel array on the SU-8 master',
        },
        {
          heading: 'Channel-Width Measurement',
          text: 'Channel widths were measured from calibrated optical micrographs: each frame carries a 100 µm scale bar that sets the Inkscape px → µm conversion. Mold 4\'s 50 µm channel came in at 50.8 µm (+1.6%), Mold 2\'s 50 µm channel at 53.2 µm (+6.3%), and Mold 3\'s 200 µm wide channel at 198.9 µm (−0.6%). The smallest features (the 20 µm Mold 1 channel) drifted +13%, at that scale, sidewall-slope and edge-pick uncertainty start to dominate the absolute error.',
          image: me382Mold50,
          caption: 'Mold 2 (nominal 50 µm) under the lithography microscope, calibrated against an on-frame 100 µm scale bar',
        },
        {
          heading: 'Lab 1 Part 2, PDMS Replica Molding',
          text: 'The patterned SU-8 master from Part 1 became the casting tool for a Sylgard 184 PDMS replica. 10:1 by mass mix (≈25 g base + 2.5 g curing agent) hand-stirred for 5 min in a weigh boat in the cleanroom hood, then poured over the master secured in a casting dish with NUFAB\'s screw-cap securing fixture.',
          image: me382PdmsMixing,
          caption: 'Hand-stirring the 10:1 Sylgard 184 mix for 5 min in the cleanroom hood',
        },
        {
          heading: 'Vacuum Degas',
          text: 'After the pour, the casting dish went into a benchtop desiccator and was pumped down for 20 min total. Stir-trapped air nucleated bubbles and built a foam layer across the surface; venting and re-pumping once partway through collapsed the foam. The Sylgard 184 datasheet calls out 15 min to 2 h to fully de-air, depending on how much air was beaten in during mixing.',
          image: me382DegasFoam,
          caption: 'Peak foam during vacuum degas, bubble field obscures the wafer until vent-and-repump knocks it down',
        },
        {
          heading: 'Cure and Demold',
          text: 'Convection-oven cure at 90 °C for 20 min (handout recipe). After cooling, the cured slab was scored with a clean razor around the perimeter of each device on the master and lifted off slowly from one corner with tweezers. The PDMS carries a negative relief, SU-8 mesas on the wafer become recessed channels in the PDMS, which will later seal against a glass cover slip to form closed microfluidic channels.',
          image: me382PdmsDemolded,
          caption: 'Wafer after demold, central PDMS device shows the imprinted spiral pattern; the surrounding cross is excess PDMS still on the master',
        },
        {
          heading: 'Dektak Profilometry',
          text: 'A stylus-profilometer line scan was taken across one SU-8 channel edge on the master: 5 µm radius stylus, 0.30 mg force, 400 µm sweep, 9000 sample points. The trace shows two flat plateaus separated by a sharp transition, bare silicon below and the top of the SU-8 film above. Averaging 200 points on each plateau gives a step height of ~20.7 µm, about 17% under the 25 µm SU-8 3025 nominal at this spin recipe, well within the expected dispense / ramp / edge-bead sensitivity for a 4-inch wafer at this thickness.',
          image: me382Profilometer,
          caption: 'Dektak stylus profilometry across an SU-8 channel edge, plateau-to-plateau step height ≈ 20.7 µm',
        },
        {
          heading: 'Lab 2 Part 1, Contact Angle on PDMS',
          text: 'At microchannel scales the surface-to-volume ratio is large enough that what happens at the wall dominates what happens in the channel, so the first half of Lab 2 measured wettability directly. A 3 µL water droplet was dispensed onto the cured PDMS replica at three different spots on the VCA sessile-drop goniometer, and the imaging software fit a tangent line at the contact point to extract the left and right contact angles. Untreated PDMS sat strongly hydrophobic in the 105 to 115 degree range, as expected for a surface covered in non-polar Si-CH₃ groups.',
        },
        {
          heading: 'O₂ Plasma and the Hydrophilic Flip',
          text: 'The PDMS substrate was then dosed with oxygen plasma. The plasma converts surface methyl groups into silanols (Si-OH), turning the top molecular layer of the PDMS into something that behaves like glass. Re-running the sessile-drop measurement immediately after treatment showed the contact angle collapse into the low tens of degrees: the same surface that had beaded water minutes earlier now nearly wet completely. The effect is transient, since mobile polymer chains diffuse fresh methyl groups back to the surface over hours, which is exactly why the next step has to happen right away.',
        },
        {
          heading: 'Microchannel Bonding',
          text: 'Each PDMS chip was cut from the slab, inlet and outlet holes were drilled at the channel ends, and both the PDMS and a glass coverslip went through O₂ plasma together. Plasma-activated silanols on the two pieces condense into covalent Si-O-Si bonds when they come into contact, so the pieces were pressed together immediately and baked at 90 °C for 15 minutes. The result is an irreversibly sealed microchannel with a hydrophilic interior, which is exactly what the next experiment wants: capillarity pulls water into the channel instead of fighting against trapped air at constrictions.',
        },
        {
          heading: 'Lab 2 Part 2, microPIV through the Sealed Device',
          text: 'With the device bonded, Lab 2 Part 2 measured the actual flow inside it. A syringe pump pushed a dilute suspension of 530 nm Nile-Red fluorescent beads through the channel at controlled rates from 0.1 to 1 µL/min. The fluorescence microscope used a dichroic filter cube to send 510 to 525 nm excitation light down to the channel and pass the 560 nm bead emission back up to the camera, exploiting the Stokes shift to reject wall reflections and pull a clean particle signal off a dark background. NIS-Elements handled focusing under a 20x objective; Andor SOLIS captured kinetic series at 25 fps with a 15 ms exposure, exporting paired TIFFs into PIVview2C for cross-correlation.',
        },
        {
          heading: 'PIV Cross-Correlation',
          text: 'Inside PIVview2C the frame pair runs through a 5x5 median filter and threshold to suppress background noise, then a mask isolates the channel interior. The actual velocity recovery is a multi-pass cross-correlation: 64x64 pixel interrogation windows with a 20 pixel step and 68 percent overlap, three iterative passes, and a three-point Gauss sub-pixel peak fit. A length-scale calibration against a known channel feature converts the pixel displacements to physical velocity, and a data-smoothing pass on the resulting vector field cleans up outliers before computing the mean profile.',
        },
        {
          heading: 'Lab 3a, AFM Tapping Mode and Fractal Roughness',
          text: 'Lab 3 moved the device off the optical bench and onto a Bruker DimensionEdge AFM. In tapping mode the cantilever is oscillated near its resonance and the tip taps the surface once per cycle, and the feedback loop adjusts the piezo height to keep the oscillation amplitude pinned at a setpoint. The benefit of tapping over contact mode is that the tip never drags laterally across the sample, so soft polymers like PDMS image cleanly without smearing. Three nested scans of the same patch at 15, 7.5, and 1.5 µm field-of-view sampled the surface across nearly a decade of length scales.',
        },
        {
          heading: 'Structure-Function Analysis',
          text: 'Classical roughness numbers like Ra and Rq depend on the scan size, so the lab instead treats the surface as a self-affine fractal and extracts a scale-independent fractal dimension D. For each tapping-mode scan, the structure function S(τ), defined as the mean squared height difference between two points separated by distance τ, was computed line-by-line and averaged across all rows. Plotting log S versus log τ gave a straight line over the valid range, and the slope yielded the Hurst exponent H through S(τ) ~ τ^(2H). The fractal dimension follows directly as D = 3 - H, with values around 2 for a smooth surface and approaching 3 for a maximally rough one.',
        },
        {
          heading: 'Lab 3b, Point Force Spectroscopy on PDMS',
          text: 'The same AFM was then re-tasked as a femtonewton force probe. With the tip parked over a single point, the piezo ramps the tip into the surface and back out while the photodiode records cantilever deflection against z position. The first ramp ran on a fused-quartz standard, which is effectively infinitely rigid: every nanometer of piezo motion shows up as one nanometer of cantilever bend, and the slope of that line calibrates the photodiode sensitivity from volts to nanometers. Force then follows from Hooke\'s law with the SCANASYST-AIR cantilever\'s nominal spring constant.',
        },
        {
          heading: 'Hertz Fit for Young\'s Modulus',
          text: 'With the sensitivity calibrated, the standard was swapped out for the cured PDMS sample and the ramp re-run. The PDMS approach curve is no longer linear in the contact region, because some of the piezo motion now goes into denting the rubber instead of bending the cantilever. The indentation depth δ at each point is the piezo travel minus the cantilever deflection. Fitting F versus δ with the Hertzian sphere-on-flat model, with a tip radius around 2 to 12 nm and a Poisson ratio of 0.4 to 0.5 for PDMS, returns the elastic modulus E directly. The retract curve picks up an additional pull-off dip from adhesion, which separates from the modulus fit and gives an adhesion force estimate on its own.',
        },
      ],
      downloads: [
        { label: 'Lab 1 Part 1 Report (SU-8 Photolithography)', url: me382Lab1Part1Pdf },
        { label: 'Lab 1 Part 2 Report (PDMS Replica)', url: me382Lab1Part2Pdf },
      ],
    },
  },

  // -------------------------------------------------------------------------
  // TIER 1, Hardware design DNA: PCB / analog / IC / open silicon
  // -------------------------------------------------------------------------

  {
    id: 32,
    priorityIndex: 10,
    title: 'LOAM: Activity-Aware Battery-Free Soil-Moisture Platform',
    description:
      'A battery-free, self-powered LoRa soil-moisture sensor designed for large-scale farms. Each buried node runs on harvested solar and banks enough charge in a single capacitor to keep measuring for up to 21 days at a stretch. Instead of asking farmers to install fixed gateways across thousands of acres, the nodes hand off their data over a 1 km LoRa link to a small basestation mounted on the farmer\'s truck, so retrieval rides on the daily driving routine. The whole node comes in at <strong>under $35</strong> in parts. Published as arXiv 2604.26303 with co-authors Omar Kamil, Thekra Alkadee, and Nivedita Arora.',
    course: 'VAK Embodied Systems Lab, 2024 to 2026',
    image: loamFigure,
    imageContain: true,
    imageBgWhite: true,
    tag: ['All', 'Electrical Engineering', 'Embedded Systems'],
    detail: {
      overview:
        'LOAM is a low-cost, low-power, activity-aware soil-moisture sensing platform built for large-scale farming. The battery-free buried nodes harvest solar energy into a single capacitor and stream measurements over LoRa to a mobile basestation that the farmer already drives past on a normal day in the field. Because the basestation is mounted to an ordinary farming vehicle, data retrieval rides on existing field traffic and the whole platform deploys without any fixed gateway infrastructure. A predictable finite-state-machine handshake bridges the intermittent connection windows between each node and the passing basestation.',
      tech: [
        'Galvanic soil-moisture probe',
        'High-impedance analog front-end',
        'Solar harvesting + capacitor storage',
        'LoRa @ 2 dBm',
        'Finite-state-machine handshake protocol',
        'Mobile basestation (vehicle-mounted)',
        'KiCad PCB',
        'C / embedded firmware',
        'Farmer-facing dashboard',
      ],
      highlights: [
        'Each node is battery-free, running entirely on harvested solar energy stored in a capacitor.',
        'A single capacitor charge sustains continuous operation for up to 21 days.',
        'The galvanic soil-moisture probe drives a high-impedance analog front-end, so the sensing chain involves no batteries and no chemistry.',
        'Indoor sensor stability has held over 70 days of bench testing.',
        'The LoRa link reaches the basestation reliably at 1 km while transmitting at only 2 dBm.',
        'An activity-aware FSM handshake triggers a data exchange whenever the basestation rolls within range, leveraging the farmer\'s normal vehicle routine for retrieval.',
        'The architecture deploys without any fixed gateway infrastructure on the farm.',
        'The bill of materials comes in at less than $35 per node.',
        'Co-authored with Omar Kamil, Thekra Alkadee, and Nivedita Arora.',
        'Published as arXiv 2604.26303.',
      ],
      links: [
        { label: 'View on arXiv', url: 'https://arxiv.org/abs/2604.26303', icon: 'external' },
      ],
      images: [loamFigure, loamAssembly],
    },
  },

  // Robotic Hand (existing) elevated to priority 30
  {
    id: 29,
    priorityIndex: 30,
    title: 'Robotic Hand Control System',
    description:
      'A robotic hand with a 2-DOF wrist, a 4-DOF dexterous finger that can curl and pinch, and a 1-DOF power finger built for force. I led the electrical side of the project: the custom GaNFET motor driver, the 14-bit magnetic encoder boards on every joint, the load cells that close the force loop, and the palm board that aggregates all of it onto a single CAN bus. Cascaded PID with anti-windup runs on the tendon forces, giving the hand a compliant, lifelike feel rather than the stiff snap of a normal servo.',
    course: 'RDS, Robot Design Studio',
    image: roboticHandGif,
    tag: ['All', 'Electrical Engineering', 'Embedded Systems'],
    gitUrl: 'https://github.com/NU-RDS/rds25-project',
    detail: {
      overview:
        'A sophisticated robotic hand that combines a 2-DOF wrist, a 4-DOF dexterous finger, and a 1-DOF power finger into a single integrated platform. The electrical subsystem uses distributed Teensy microcontrollers communicating over CAN bus, 14-bit AS5047P magnetic encoders for joint position, NAU7802 load cells for force feedback, and Series Elastic Actuators for compliant force control. ODrive Pro controllers handle the FOC motor work, and a React frontend backed by Django gives the operator either manual slider control or automatic webcam-based hand tracking through Mediapipe.',
      tech: [
        'Teensy 4.1',
        'CAN Bus',
        'SPI',
        'Series Elastic Actuators',
        'ODrive',
        'GaN power electronics',
        'AS5047P',
        'NAU7802',
        'Altium',
        'React',
        'Django',
        'Mediapipe',
        'OpenCV',
        'C++',
        'Python',
      ],
      highlights: [
        'Cascaded PID with anti-windup closes the tendon force-control loop.',
        'A custom tendon Jacobian matrix handles the 4-DOF dexterous finger kinematics.',
        'Sixteen 14-bit AS5047P magnetic encoders report joint position over SPI, distributed across four Teensy MCUs.',
        'NAU7802 load cells were characterized with experimental force-deflection calibration, producing both linear and quadratic SEA models.',
        'The custom GaNFET motor driver runs two motors from a 12 to 24 V input at 24 A continuous and 40 kHz PWM.',
        'Custom 12 mm circular encoder PCBs replaced the oversized off-the-shelf boards.',
        'The React and Django GUI supports manual slider control and live webcam hand tracking through Mediapipe.',
        'A palm aggregation board ties the encoder SPI buses and CAN bus together for system-level testing.',
      ],
      images: [rdsBanner, rdsMotorDriver, rdsMotorDriver3d, rdsEncoder, rdsPalmAnnotated, rdsControlArch, rdsSystemDiagram, rdsG2, rdsG1, rdsG3, rdsG4],
      story: [
        {
          heading: 'The Problem: Too Many Boxes',
          text: 'When I joined the electrical team, the system was a patchwork of separate boards, an ODrive for motor control with finicky SPI connectors and limited power output, a standalone Teensy breakout with a large footprint, a separate power distribution unit, and oversized pre-fab encoders that didn’t fit the mechanical team’s 10mm form factor requirement. Debugging meant tracing wires across five different boards from three different design generations. We needed to consolidate.',
          image: rdsSystemDiagram,
          caption: 'The original architecture, a distributed mess of separate boards and wire harnesses',
        },
        {
          heading: 'Our Design Goal: Modular PCB Stack',
          text: 'We restructured the entire electrical system into domain-specific, stackable PCBs: a power domain (24 V → 5 V regulation), a switching domain (40 kHz GaNFET gate driving), and a logic/communication layer (SPI, CAN, MCU interfacing). Each board could be debugged and replaced independently. We defined four fallback plans, from our custom motor driver down to reverting to ODrive, because hardware never works the first time.',
          image: rdsControlArch,
          caption: 'The modular control architecture: power, switching, and logic domains separated by function',
        },
        {
          heading: 'Custom GaNFET Motor Driver',
          text: 'Charlie designed a custom GaNFET motor driver capable of driving 2 motors at 24 A continuous with 40 kHz PWM. The 3D render looked beautiful. The real board looked even better, until it caught fire during testing due to a suspected short or thermal issue. We’re still debugging with TI support. In the meantime, we fell back to ODrive for the final demo, which is exactly why we planned backup options from day one.',
          image: rdsMotorDriver3d,
          caption: 'Charlie’s custom GaNFET motor driver,3D render from Altium',
        },
        {
          heading: 'The Encoder Problem',
          text: 'The mechanical team needed 16 AS5047P magnetic encoders in a 10mm form factor. The pre-fab boards from Mouser were too large. I designed custom 12 mm circular encoder PCBs that could be soldered directly to vias. We also attempted 10 mm rectangular boards with 5-pin SPI, but a wiring error bridged MOSI to GND instead of 5V. The bigger surprise: after connecting 4 encoders to a single Teensy, SPI would go unresponsive. Each additional encoder added parasitic capacitance to the MISO line. We ended up needing 4 separate Teensy MCUs to handle 16 encoders, a MUX IC that physically disconnects MISO lines is recommended for the next iteration.',
          image: rdsEncoder,
          caption: 'Pre-fab encoder board (left) vs our custom 12 mm circular encoder (magnet shown for scale)',
        },
        {
          heading: 'The Palm Board',
          text: 'Han designed the palm board, a sensor aggregation PCB that sits in the robot’s palm and collects data from all joint encoders over SPI, then communicates upstream via CAN bus. The annotated pinout shows 8 SPI chip-select pins for individual encoder addressing, CAN bus lines, and 24 V power input. This board became the backbone of our sensing architecture after we abandoned the original distributed encoder wiring.',
          image: rdsPalmAnnotated,
          caption: 'Han’s palm board with annotated pinout, SPI encoder aggregation and CAN bus communication',
        },
      ],
      downloads: [
        { label: 'Motor Driver Documentation', url: rdsMotorDriverDoc },
        { label: 'Motor Driver Schematic', url: rdsMotorDriverSchematic },
      ],
    },
  },

  // NFC Temperature Sensor (existing id 18) elevated to priority 40
  {
    id: 18,
    priorityIndex: 70,
    title: 'Mixed-Signal NFC Temperature Sensor in 45 nm CMOS',
    description:
      'A full-custom mixed-signal IC: an NFC-powered wireless temperature sensor designed from the transistor level up in a 45 nm CMOS process. The chip has no battery. When an NFC reader gets close, the chip harvests power directly from the reader\'s field, measures its own temperature, and sends the digital reading back through the same wireless link. The analog front-end, the temperature-to-digital conversion, and the NFC harvesting and data link were all drawn schematic to layout in Cadence Virtuoso and characterized with SPICE.',
    course: 'CE392, VLSI Systems Design',
    image: nfcSchematicSim,
    tag: ['All', 'Computer Engineering', 'Electrical Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'A mixed-signal integrated circuit design for an NFC-powered wireless temperature sensor in a 45 nm CMOS process. The chip combines analog temperature-sensing circuitry with a digital readout path and an NFC harvesting and communication front-end, all drawn from schematic to layout in Cadence Virtuoso and validated with SPICE.',
      tech: ['45 nm CMOS', 'Cadence Virtuoso', 'Mixed-Signal', 'NFC', 'Analog Design', 'SPICE', 'Verilog-A'],
      highlights: [
        'The analog blocks were laid out full-custom in a 45 nm CMOS process.',
        'A temperature-to-digital conversion circuit turns the sensed value into a transmittable digital reading.',
        'The NFC front-end both harvests power from the reader\'s field and modulates the data link back.',
        'Schematic capture and simulation ran in Cadence Virtuoso end to end.',
        'Transient and AC analyses across multiple signal domains validated the mixed-signal design.',
      ],
      images: [nfcSchematicSim, nfcAnalogWaveforms, nfcTransient, nfcDigitalTiming, nfcOutputSignals, nfcBlockDiagram, nfcLayout, nfcSim2, nfcSim3, nfcSim4, nfcSim5],
    },
  },

  // 6T SRAM Array (existing id 31) elevated to priority 50
  {
    id: 31,
    priorityIndex: 60,
    title: '6T SRAM Array in 45 nm CMOS',
    description:
      'A 4 by 4 SRAM array designed from the transistor level in a 45 nm CMOS process. Every 6T bitcell, the precharge circuit that levels the bitlines before each read, the write driver, and the clocked sense amplifier that resolves the small differential read voltage were all drawn by hand in Cadence Virtuoso. The bitcell and precharge modules both pass DRC and LVS cleanly, and the full array reads and writes correctly in simulation at a 1 V supply and a 10 ns clock period.',
    course: 'CE391, VLSI Design I',
    image: ce391Banner,
    tag: ['All', 'Computer Engineering', 'Electrical Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'A 4 by 4 SRAM array designed from the transistor level in Cadence Virtuoso, targeting a 45 nm CMOS process. The design steps through a 6-transistor bitcell with carefully sized pull-down, access, and pull-up transistors, plus the precharge circuit, the write logic, and a clocked sense amplifier needed to read and write the array. The bitcell and precharge modules both pass full DRC and LVS verification.',
      tech: ['Cadence Virtuoso', '45 nm CMOS', 'SRAM', 'DRC/LVS', 'SPICE'],
      highlights: [
        'The 6T bitcell uses a 180 nm pull-down, a 135 nm access transistor, and a 90 nm pull-up.',
        'Each bitcell takes up 26.3 µm² of die area (4.79 by 5.5 µm).',
        'A precharge circuit with an equalization transistor levels the bitlines before each read.',
        'A clocked sense amplifier resolves the small differential voltage on the bitlines into a digital one or zero.',
        'The bitcell and precharge modules both pass DRC and LVS checks cleanly.',
        'A simulation testbench verifies read and write operation at 1 V and a 10 ns clock period.',
      ],
      images: [ce391Banner, ce391G1, ce391G2, ce391G3, ce391G4, ce391G5, ce391G6, ce391G7],
      downloads: [{ label: 'Lab Report', url: sramReportPdf }],
    },
  },

  // -------------------------------------------------------------------------
  // TIER 2, Open silicon (TinyTapeout): semiconductor design DNA on real fabs
  // -------------------------------------------------------------------------

  {
    id: 35,
    priorityIndex: 15,
    title: 'TinyTapeout: Open-Source Silicon Workshop',
    description:
      'I designed and led a hands-on workshop that taught Northwestern students how to design their own real silicon chip. Seventy-six students attended in person across three sessions and twelve hours of curriculum, and the workshop reached <strong>152 students</strong> in total. Students came from engineering, computer science, mechanical, and materials backgrounds, and many went from never having seen a chip layout to submitting their own designs for fabrication. I secured IEEE funding to sponsor <strong>three student designs and a custom PCB</strong> for the May 2026 tape-out, with the winning entries picked by group vote at the end of the workshop.',
    course: 'IEEE Northwestern, April 2026',
    image: ttWorkshopGroup,
    tag: ['All', 'Computer Engineering'],
    gitUrl: 'https://github.com/jackthoene/TinyTapeout',
    detailUrl: '/tinytapeout',
  },

  {
    id: 36,
    priorityIndex: 17,
    title: 'Frogger: Single-Tile VGA Game on Sky130',
    description:
      'A playable Frogger game built entirely in hardware and submitted for fabrication on the Sky130 TTSKY26a shuttle. The whole game (graphics, physics, audio, scoring, every sprite) fits in a single 167 by 108 µm tile of silicon with no RAM, no framebuffer, no CPU, no multiplier, and no divider. Every pixel of every frame is rendered live at 60 Hz from the frog\'s position and the lane patterns around it. Plug a VGA monitor and an SNES controller into the eval board and you can play it on the chip.',
    course: 'TinyTapeout TTSKY26a Shuttle, 2026',
    image: froggerCard,
    overlayImage: froggerTtOverlay,
    overlayCaption: 'TT layout',
    imageContain: false,
    imageBgWhite: false,
    tag: ['All', 'Computer Engineering'],
    gitUrl: 'https://github.com/jackthoene/sky130-frogger',
    detail: {
      overview:
        'A hardware Frogger clone built for the Tiny Tapeout TTSKY26a Demoscene Competition. The entire game (graphics, physics, lives, scoring, audio) fits in a single 167 by 108 µm Sky130 tile, with no multipliers, dividers, RAM, or framebuffer. All graphics are procedural and computed per pixel in real time. Obstacle patterns use power-of-2 period bitmasks to get free modulo on lane positions, and the frog sprite (eyes, pupils, belly, legs) is drawn entirely from coordinate comparisons with no ROM. Audio rides on a 20-bit DDS accumulator on the TT Audio PMOD.',
      tech: [
        'Verilog',
        'SkyWater Sky130',
        'OpenLane',
        'VGA (640×480 @ 60 Hz)',
        'Gamepad PMOD (SNES protocol)',
        'TT Audio PMOD',
        'DDS audio synthesis',
      ],
      highlights: [
        'The whole game lives on a single 1 by 1 Sky130 tile and uses 152 flip-flops in total.',
        'The design has no multipliers, no dividers, no RAM, and no framebuffer.',
        'Sprites are rendered procedurally, one pixel at a time, with no stored bitmap data.',
        'Period-128 obstacle bitmasks let lane patterns wrap as free modulo operations.',
        'Logs carry an 8-pixel collision tolerance so the gameplay stays forgiving.',
        'A 20-bit DDS audio synthesizer drives the hop, death, and goal cues.',
      ],
      links: [
        { label: 'View Source', url: 'https://github.com/jackthoene/sky130-frogger', icon: 'github' },
        { label: 'View GDS Layout', url: 'https://jackthoene.github.io/sky130-frogger/', icon: 'cube' },
        { label: 'Play in Browser', url: '/frogger-play', icon: 'play' },
      ],
    },
  },

  // -------------------------------------------------------------------------
  // TIER 3, Adjacent IC / cleanroom-fab / digital design
  // -------------------------------------------------------------------------

  {
    id: 34,
    priorityIndex: 40,
    title: 'MANTIS: Manufacturable Hyperspectral Filter Framework',
    description:
      'A framework for designing tiny, manufacturable color filters that let a normal image sensor see almost as much as a full hyperspectral camera, but without the cost or computational overhead. The idea was inspired by the mantis shrimp, which has sixteen different photoreceptor types in its eye instead of the three humans have. Rather than measuring every wavelength of light continuously, MANTIS learns the handful of wavelengths that matter most for a given task (say, telling ripe avocados from unripe ones) and designs a narrowband filter targeted at those wavelengths. The whole pipeline currently runs in simulation against open-source hyperspectral datasets; no physical filter has been fabricated yet. <strong>Awarded Best Poster, ACM SenSys 2026 / IEEE IoT Week</strong> in Saint Malo, France, May 2026.',
    course: 'VAK Embodied Systems Lab and CS353, 2024 to 2026',
    image: mantisPoster,
    imageContain: true,
    imageBgWhite: true,
    tag: ['All', 'Electrical Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'MANTIS is a methodology and framework for designing application-specific narrowband optical filters that would augment a normal image sensor with hyperspectral-like performance, without paying the complexity, cost, and computational overhead of full hyperspectral acquisition. The bio-inspiration started in CS353 (Computer Vision) with a computational simulation of the mantis shrimp\'s visual system, sixteen photoreceptor types arranged on a hexagonal compound eye, and that became the motivating prior for treating the spectrum as a small, task-specific set of samples rather than a continuous cube. The current work is simulation-only: it identifies task-relevant anchor wavelengths from open-source pushbroom HSI datasets, models the resulting multi-bandpass filter, and runs the full inference-in-sensor pipeline end-to-end in software. No physical filter has been fabricated to date.',
      tech: [
        '3D CNN (PyTorch)',
        'Hyperspectral datasets (UofH Houston 2013, Avocado, Indian Pines)',
        'Dynamic programming (anchor selection)',
        'Piecewise-polynomial templates',
        'Class-weighted k-NN',
        'Chromatic focal stack / shape-from-defocus (modeled)',
        'Python / NumPy / OpenCV',
      ],
      highlights: [
        'In simulation, MANTIS reaches 75 percent classification accuracy on Houston 2013, 72 percent on Avocado, and 67 percent on Indian Pines, approaching CNN baselines with three orders of magnitude less data per pixel.',
        'Inference collapses from a million-dimensional HSI cube down to twelve dimensions of linear algebra plus a table lookup, making an on-device classifier feasible on a microcontroller.',
        'Dynamic programming over a 249-band HSI cube selects three narrowband anchor wavelengths between 400 and 1000 nm to drive the filter design.',
        'Per-class spectral templates are built from pushbroom HSI scenes and treated as a design-time analytical resource rather than a deployment platform.',
        'The bio-inspiration came from a CS353 simulation of the mantis shrimp\'s sixteen-photoreceptor compound eye.',
        'Feasibility was demonstrated by learning and simulating the multi-bandpass mask on open-source datasets; no physical filter has been fabricated yet.',
        'Awarded Best Poster, ACM SenSys 2026 / IEEE IoT Week in Saint Malo, France, May 2026.',
      ],
      images: [mantisPoster, mantisBanner, mantisG1, mantisG2, mantisG3, mantisG4, mantisG5, mantisG6, mantisG7, mantisG8, mantisG9, mantisG10, mantisG11, mantisG12],
      story: [
        {
          heading: 'Bio-Inspired Origin: Mantis Shrimp Vision',
          text: 'The MANTIS framework began as a computer-vision exercise in CS353: simulate the mantis shrimp\'s visual system, one of the most complex eyes in nature, with 16 distinct photoreceptor types on a hexagonal compound eye, and ask why nature picked discrete narrowband sensors instead of a continuous spectrum. The simulation modeled hexagonal compound-eye geometry and multi-spectral sensitivity across the 16 receptor types, and the punchline carried directly into MANTIS: spectrum is most useful when sampled at a small set of task-relevant wavelengths, not measured continuously. That insight is the prior that motivates the entire pipeline.',
          image: mantisBanner,
          caption: 'CS353 mantis-shrimp visual system simulation,16 photoreceptor types on a hexagonal compound eye',
        },
        {
          heading: 'The MANTIS Pipeline (Simulation)',
          text: 'A traditional pushbroom HSI scene is treated as a design-time analytical resource rather than a deployment platform: per-class mean spectra and feature-importance scoring surface a small set of discriminating wavelengths. Dynamic programming over per-class reflectance templates picks anchor wavelengths that minimize between-class piecewise-polynomial fit error. The proposed deployment hardware would encode those anchors directly as a multi-bandpass filter providing a well-posed prior for chromatic-focal-stack unmixing, with recovery becoming a closed-form 3×3 linear inverse rather than full-cube reconstruction, and the recovered values feeding a 12-D shape descriptor classified with class-weighted k-NN. The whole pipeline runs end-to-end in simulation against open-source datasets; no filter has been physically built.',
          image: mantisPoster,
          caption: 'MANTIS poster, ACM SenSys 2026 / IEEE IoT Week (Saint Malo, France, May 2026)',
        },
      ],
      downloads: [
        { label: 'Mantis Shrimp Vision Presentation (CS353)', url: mantisPresentationPdf },
      ],
    },
  },

  // CNN Inference Accelerator (existing id 2) at priority 110
  {
    id: 2,
    priorityIndex: 80,
    title: 'CNN Inference Accelerator: RTL to Layout',
    description:
      'A convolutional neural network accelerator taken end to end from a trained TensorFlow model down to a clean silicon layout. The CNN runs entirely in custom hardware (no CPU, no software), with a 2D array of multiply-accumulate units doing the convolution work in parallel and dedicated SystemVerilog modules handling pooling, flattening, and the fully connected output layer. A Python golden model and a custom 8-bit fixed-point quantization pipeline back the verification effort, which runs at three levels (functional, post-synthesis, post-layout) and ends in a layout that passes DRC and LVS cleanly.',
    course: 'CE493, Advanced Low Power IC Design',
    image: cnnBanner,
    tag: ['All', 'Computer Engineering'],
    gitUrl: 'https://github.com/lucahhot/CE493_Project_CNN',
    detail: {
      overview:
        'A hardware chip implementing a convolutional neural network centered on a 2D array of multiply-accumulate processing elements. The full pipeline trains a CNN in TensorFlow, quantizes its weights and activations to 8-bit fixed point, then implements the same network in SystemVerilog RTL with dedicated convolution, max pooling, flattening, and fully connected layers. A Python golden model produces expected outputs for fifteen test samples, which are validated against the RTL simulation at both the schematic and post-synthesis levels.',
      tech: ['SystemVerilog', 'Python', 'TensorFlow', 'Jupyter', '8-bit Quantization', 'MAC Array', 'RTL', 'Cadence'],
      highlights: [
        'A 2D MAC array runs the 4 by 4 kernel convolutions in parallel.',
        'The network takes a 28 by 28 binary image and extracts three feature channels per layer.',
        'A custom Python pipeline quantizes the trained TensorFlow model down to 8-bit fixed point without destroying accuracy.',
        'The full RTL stack includes the ConvolutionPE, PoolingModule, FlatteningModule, and FullyConnectedModule.',
        'Three testbench levels (functional, post-synthesis, and post-layout) verify the design against the golden model.',
        'A fifteen-sample verification suite compares hardware outputs to the Python golden model at every stage.',
      ],
      images: [cnnBanner, cnnG22, cnnG1, cnnG12, cnnG29, cnnG30, cnnG31, cnnG32, cnnG33, cnnG3, cnnG4, cnnG5, cnnG6, cnnG7, cnnG8, cnnG9, cnnG10, cnnG11, cnnG13, cnnG14, cnnG15, cnnG16, cnnG17, cnnG18, cnnG19, cnnG20, cnnG21, cnnG23, cnnG24, cnnG2, cnnG25, cnnG26, cnnG27, cnnG28],
      story: [
        {
          heading: 'From Software to Silicon',
          text: 'The challenge was straightforward but deep: take a convolutional neural network that runs in TensorFlow on a GPU and implement the same inference pipeline entirely in custom hardware. No CPU, no software, just gates and flip-flops. The first step was training a CNN in Python for binary image classification on 28×28 pixel inputs, then quantizing all weights and activations from 32-bit floating point down to 8-bit fixed point without destroying accuracy.',
          image: cnnG22,
          caption: 'CNN architecture: Conv → Pool → Conv → Pool → Flatten → Fully Connected → Output',
        },
        {
          heading: 'The MAC Array',
          text: 'The heart of the design is a 2D array of Multiply-Accumulate (MAC) processing elements that perform the convolution operation in parallel. Each ConvolutionPE takes a 4×4 kernel and slides it across the input feature map, accumulating partial sums. Three parallel feature channels extract different learned patterns from the input image. The MAC outputs feed into max pooling, flattening, and fully-connected modules, all implemented in synthesizable SystemVerilog.',
        },
        {
          heading: 'Quantization: The Hard Part',
          text: 'Moving from floating-point TensorFlow to 8-bit fixed-point hardware is where most CNN-to-silicon projects die. We built a custom quantization pipeline in Jupyter that maps each layer’s weight distribution to an optimal fixed-point representation, tracking quantization error at every stage. The Python golden model generates expected outputs for 15 test samples, which we validate against the RTL simulation at three levels: functional, post-synthesis, and post-layout.',
        },
        {
          heading: 'The Final Layout',
          text: 'After synthesis through Cadence tools targeting a standard cell library, the design was placed and routed into the layout shown in the gallery. The colored blocks represent different functional modules, convolution PEs, memory banks, pooling logic, and the fully-connected classifier. The final design passed DRC and LVS verification.',
          image: cnnBanner,
          caption: 'Post-layout view of the CNN accelerator, colored by functional module',
        },
      ],
      downloads: [
        { label: 'Presentation', url: cnnPresentationPdf },
        { label: 'Report', url: cnnReportPdf },
      ],
    },
  },

  // V4 Engine / CAM (existing id 30) at priority 120, mechatronics fab
  {
    id: 30,
    priorityIndex: 50,
    title: 'Injection-Molded V4 Engine Components',
    description:
      'A working toy V4 engine produced through real injection-mold manufacturing. Our team handled the smallest and most precise components: the crankshaft, the connecting rods, and the pistons. We designed a single family mold that produces all three parts in one shot, machined the mold steel ourselves on a CNC mill, and used a bent U-shape clamp connecting rod with an interference fit on the crankshaft and a snap fit on the piston. Statistical process control on the production run validated dimensional accuracy across every critical feature.',
    course: 'ME340-2, Computer-Aided Manufacturing',
    image: camBanner,
    tag: ['All', 'Mechanical Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'A manufacturing engineering project designing and fabricating the precision components of a toy V4 engine. Our team built the crankshaft, connecting rods, and pistons, all injection molded on a single family mold. The connecting rod uses a bent U-shape clamp design with a 0.128 inch interference fit to the crankshaft and a snap fit to the piston. Statistical process control was applied to the production run to validate dimensional accuracy across every part.',
      tech: ['NX CAD', 'CNC', 'Injection Molding', 'SPC', 'Python', 'LaTeX'],
      highlights: [
        'A single family mold produces the crankshaft, connecting rods, and pistons in one injection cycle.',
        'The connecting rod ties to the crankshaft through an interference fit and to the piston through a snap fit.',
        'The piston is 0.650 inches long and 0.370 inches in diameter with a 0.005 inch running clearance in the case.',
        'The crankshaft has a 0.128 inch coupling diameter and 2.123 inches of internal spacing between couplings.',
        'The full SPC workup includes histograms, box-violin plots, run charts, and correlation studies on the critical dimensions.',
        'Mold steel was CNC-machined in-house on a Haas Super Mini Mill 2.',
        'A LaTeX report with 49 embedded figures documents the full design and production process.',
      ],
      images: [camBanner, camG7, camG8, camG9, camG10, camG16, camG17, camG18, camG15, camG23, camG24, camG11, camG12, camG25, camG22, camG21, camG13, camG14, camG20, camG19, camG1, camG2, camG3, camG4, camG5, camG6, camG26],
      story: [
        {
          heading: 'The Challenge: Precision at Miniature Scale',
          text: 'Our team took on the hardest components of the V4 engine, the crankshaft, connecting rods, and pistons. These are the smallest parts with the tightest tolerances. The crankshaft coupling diameter is 0.128" with an internal spacing of exactly 2.123" between couplings. The piston needs 0.005" clearance inside the case. At these scales, a 0.002" error means the engine doesn’t turn.',
          image: camG16,
          caption: 'CAD model of the crankshaft,0.128" coupling diameter, 2.123" internal spacing',
        },
        {
          heading: 'Connecting Rod Design Evolution',
          text: 'The connecting rod went through a major redesign. Our initial concept used a ball socket joint, but analysis showed it would be nearly impossible to injection mold at this scale with the available tooling. We pivoted to a two-sided clamp design with a bent U-shape, the clamp edges extend 0.05" beyond the shaft, allowing free rotation while preventing the shaft from falling out. The interference fit to the crankshaft and snap-fit to the piston were tuned through multiple test molds.',
          image: camG24,
          caption: 'Connecting rod drawing, bent U-shape clamp design with 0.128" internal diameter',
        },
        {
          heading: 'Family Mold Design',
          text: 'Rather than creating separate molds for each component, we designed a single family mold that produces the crankshaft, connecting rods, and pistons in one injection cycle. This reduced total cycle time but required careful runner and gate placement to ensure uniform fill across parts with very different geometries. The core and cavity were CNC-machined on a Haas Super Mini Mill 2.',
          image: camG20,
          caption: 'CNC-machined injection mold on the Haas Super Mini Mill 2',
        },
        {
          heading: 'Statistical Process Control',
          text: 'After production, we ran a full SPC analysis on the molded parts. Histograms and box-violin plots for each component’s critical dimensions, run charts for process stability monitoring, and correlation analysis between machining parameters and dimensional accuracy. The analysis confirmed our process was stable and within tolerance for the rotational mechanism to function.',
          image: camG26,
          caption: 'Histogram analysis of critical dimensions across production run',
        },
      ],
    },
  },

  // -------------------------------------------------------------------------
  // TIER 4, Embedded systems & FPGA verification
  // -------------------------------------------------------------------------

  // Smartwatch (existing id 1) at priority 130
  {
    id: 1,
    priorityIndex: 130,
    title: 'Smartwatch with Face-Unlock',
    description:
      'A custom smartwatch built from a hand-laid-out PCB. It pairs an ESP32 camera module with a Cortex-M4 microcontroller and a BNO055 IMU under an LVGL touchscreen UI. The watch streams its camera feed over Wi-Fi to a backend running OpenCV face detection, then a dlib face-recognition pipeline unlocks the device when it sees a familiar face. A separate Flask service renders the IMU orientation in 3D, and a Next.js dashboard ties the live feeds together. Inference can run on the edge or offload to AWS Lambda.',
    course: 'EE327, Electronic System Design II',
    image: smartwatchImg,
    tag: ['All', 'Electrical Engineering', 'Embedded Systems'],
    gitUrl: '/',
    detail: {
      overview:
        'An integrated smartwatch system combining custom PCB hardware, face recognition, camera streaming, IMU tracking, and a web interface. The ESP32-CAM module streams video over Wi-Fi to a Tornado WebSocket server running OpenCV Haar cascade face detection. A separate Flask server renders 3D IMU orientation from pitch, yaw, and roll data. The cam-website frontend is a Next.js React application with Tailwind CSS providing responsive live camera feeds.',
      tech: ['ESP32', 'LVGL', 'PlatformIO', 'OpenCV', 'Tornado WebSockets', 'Flask', 'Next.js', 'Tailwind CSS', 'Python', 'dlib'],
      highlights: [
        'The smartwatch runs on a custom PCB with Teensy and Arduino firmware on board.',
        'An ESP32-CAM module streams live video over Wi-Fi to a host server using HTTP.',
        'OpenCV Haar cascades on the backend perform real-time face detection over a WebSocket connection.',
        'A dlib and sklearn face-recognition pipeline turns detections into named identities.',
        'A Flask service visualizes the IMU\'s pitch, yaw, and roll in 3D for orientation tracking.',
        'A Next.js React web interface ties the live camera feed and watch UI together in one dashboard.',
      ],
      images: [smartwatchImg],
    },
  },

  // Sobel UVM (existing id 17) at priority 140
  {
    id: 17,
    priorityIndex: 140,
    title: 'Sobel Edge Detection with UVM Verification',
    description:
      'A hardware Sobel edge detector written in SystemVerilog and verified with a full UVM testbench (sequencer, driver, monitor, scoreboard, and coverage). The design runs the 3 by 3 horizontal and vertical gradient kernels through a pipelined architecture for real-time throughput, and the UVM environment validates the output against reference images like the Brooklyn Bridge.',
    course: 'CE387, Real-Time Digital Systems Design',
    image: placeholder,
    tag: ['All', 'Computer Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'A hardware implementation of the Sobel edge detection algorithm in SystemVerilog with a complete UVM (Universal Verification Methodology) testbench. The design processes image data through horizontal and vertical gradient kernels to detect edges in real time.',
      tech: ['SystemVerilog', 'UVM', 'Image Processing', 'FPGA'],
      highlights: [
        'The 3 by 3 Sobel convolution kernels are implemented directly in RTL.',
        'A full UVM verification environment provides functional coverage of the design.',
        'The pipeline was tested against standard reference images, including the Brooklyn Bridge.',
        'The pipelined architecture gives the detector real-time throughput on streaming image data.',
      ],
    },
  },

  // CORDIC (existing id 22) at priority 150
  {
    id: 22,
    priorityIndex: 150,
    title: 'CORDIC Algorithm: Multiplier-Free Trig',
    description:
      'A SystemVerilog implementation of the iterative CORDIC algorithm for computing arctangent. Multipliers are expensive in hardware, so CORDIC swaps them out for a sequence of shifts and additions that converge on the same answer. The result is a trig core that fits comfortably on an FPGA and lets the user trade off precision against iteration count.',
    course: 'CE387, Real-Time Digital Systems Design',
    image: placeholder,
    tag: ['All', 'Computer Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'An iterative CORDIC (Coordinate Rotation Digital Computer) implementation in SystemVerilog for computing arctangent values using only shifts and additions, with no multipliers, which makes it efficient for FPGA deployment.',
      tech: ['SystemVerilog', 'FPGA', 'Fixed-Point Arithmetic'],
      highlights: [
        'The arctangent is computed without any hardware multipliers, which saves significant FPGA resources.',
        'The architecture is an iterative shift-and-add core that converges on the target angle.',
        'The precision of the result is configurable by changing the number of CORDIC iterations.',
      ],
    },
  },

  // FM Radio (existing id 15) at priority 160
  {
    id: 15,
    priorityIndex: 160,
    title: 'FPGA-Based FM Radio',
    description:
      'A real-time FM radio receiver implemented in C++ and deployed to an FPGA. The design takes raw I and Q samples coming off a USRP software-defined radio, runs them through a digital downconversion and filtering pipeline, and turns the result into audio you can listen to live.',
    course: 'CE387, Real-Time Digital Systems Design',
    image: placeholder,
    tag: ['All', 'Computer Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'A complete FM radio receiver implemented in C++ for FPGA deployment. The design takes raw I and Q samples from a USRP software-defined radio and demodulates them through a digital signal processing pipeline into audio output.',
      tech: ['C++', 'FPGA', 'DSP', 'USRP', 'FM Demodulation'],
      highlights: [
        'FM demodulation runs in real time directly on the I and Q samples coming off the USRP.',
        'A digital downconversion and filtering pipeline isolates the desired station and prepares the signal for demodulation.',
        'The demodulated signal is converted into an audio stream that drives a real speaker.',
      ],
    },
  },

  // Wi-Fi Webcam (existing id 3) at priority 170
  {
    id: 3,
    priorityIndex: 170,
    title: 'Wi-Fi Enabled Webcam',
    description:
      'A small embedded webcam built around a SAM microcontroller and an OV7740 image sensor. The microcontroller pulls frames off the sensor over DMA, then a WINC3400 Wi-Fi module serves them over HTTP to any browser on the local network. Plug it in and the live feed appears at a URL.',
    course: 'EE326, Electronic System Design I',
    image: placeholder,
    tag: ['All', 'Electrical Engineering', 'Embedded Systems'],
    gitUrl: '/',
    detail: {
      overview:
        'An embedded webcam system using a SAM microcontroller with an OV7740 image sensor. The device captures video frames via DMA transfer and streams them over Wi-Fi through a WINC3400 module, viewable from any browser on the local network.',
      tech: ['SAM MCU', 'OV7740', 'WINC3400 Wi-Fi', 'C', 'HTTP', 'DMA'],
      highlights: [
        'Frames are pulled directly off the OV7740 image sensor over a DMA channel for low CPU overhead.',
        'The WINC3400 Wi-Fi module handles the HTTP server that exposes the live feed on the network.',
        'Any browser on the local network can pull up the live view by visiting the device\'s URL.',
        'The firmware runs on the Atmel SAM platform and was written from the ground up in C.',
      ],
    },
  },

  // -------------------------------------------------------------------------
  // TIER 5, Software / ML (least relevant for hardware roles)
  // -------------------------------------------------------------------------

  // ARES (existing id 4) at priority 190
  {
    id: 4,
    priorityIndex: 190,
    title: 'Helicopter Landing Zone Optimization',
    description:
      'An extension of the ARES geospatial decision-aid platform that automates the search for safe helicopter landing zones. The pipeline pulls satellite and elevation data, filters out terrain that is too steep or too overgrown to land on, clusters what is left into contiguous regions, and then packs as many landing-zone-sized rotated boxes as possible into each region. The output is a map showing the operator exactly where a helicopter could put down safely.',
    course: 'EE475, Machine Learning',
    image: aresRaster,
    tag: ['All', 'Electrical Engineering'],
    gitUrl: '/',
    detail: {
      overview:
        'This project extends the ARES (Automated Remote Earth-observation System) decision-aid platform, originally developed by Captain Ryan Helm, USMC, as his thesis at the Naval Postgraduate School in June 2023. ARES formulates models for helicopter landing zone detection, tactical pathfinding, battlespace geometry optimization, and course of action selection. For the scope of EE475, our team focused specifically on the HLZ detection module, building the geospatial data preprocessing pipeline, the terrain filtering algorithms, the K-Means clustering for terrain segmentation, and a polygon optimization algorithm for fitting non-overlapping landing zones into viable terrain.',
      tech: ['Python', 'Google Earth Engine', 'Rasterio', 'K-Means Clustering', 'NumPy', 'Matplotlib', 'Geospatial', 'LaTeX'],
      highlights: [
        'The project builds on Captain Ryan Helm\'s ARES thesis (NPS, 2023) and tackles the HLZ detection module specifically.',
        'Geospatial preprocessing rasterizes raw .tiff satellite data into NumPy arrays through rasterio.',
        'A land-cover filter excludes grassland, tree cover, and developed areas from the candidate terrain.',
        'A slope analysis computes the gradient matrix from elevation data and rejects anything steeper than 5 degrees.',
        'Combining the land-cover and slope filters yields the dataset of barren, flat land suitable for an HLZ.',
        'A convolution pass spatially smooths the candidate regions before clustering.',
        'Polygons are then constructed around contiguous regions of usable land.',
        'A non-overlapping rotated-box fitting algorithm packs the maximum number of landing zones into each polygon.',
      ],
      story: [
        {
          heading: 'Built on ARES',
          text: '"Mission Planning Optimization for Infantry Operations" is the title of a thesis published by Captain Ryan Helm, U.S. Marine Corps, while studying Operations Research at the Naval Postgraduate School in Monterrey, California, published June 2023. His work formulates four models for helicopter landing zone detection, tactical pathfinding, battlespace geometry optimization, and course of action selection. For our EE475 final project, we focused exclusively on the HLZ detection module, building the data preprocessing and ML pipeline that takes raw geospatial data and outputs optimized landing zone placements.',
          image: aresWorkflow,
          caption: 'ARES system workflow (Helm, NPS 2023), our work focused on the HLZ detection pipeline',
        },
        {
          heading: 'Terrain Preprocessing Pipeline',
          text: 'Our pipeline starts by collecting geospatial data in .tiff format and rasterizing it into arrays manipulable for machine learning. We filter on two primary features: land coverage (excluding grassland, tree cover, and developed areas) and terrain slope (computing the gradient matrix from elevation data and filtering anything above 5 degrees). These two filters are combined to produce a dataset of barren, flat land, the only terrain feasible for helicopter landing zones.',
          image: aresRaster,
          caption: 'Five raster layers from our preprocessing: satellite imagery, elevation, slope, land cover, and roads',
        },
        {
          heading: 'Polygon Optimization',
          text: 'After filtering and clustering, contiguous regions of feasible terrain are bounded by polygons. The core algorithmic challenge we solved: given a polygon of suitable terrain, find the maximum number of non-overlapping rotated boxes (helicopter landing zones) of user-defined minimum size that fit within the boundary. The algorithm tests bounding boxes at multiple rotation angles to maximize the number of HLZs that can be placed in viable terrain.',
          image: aresPolygon,
          caption: 'Polygon with interior grid points and non-overlapping rotated HLZ boxes (green)',
        },
      ],
      images: [aresWorkflow, aresRaster, aresPolygon, aresClustering, aresCluster1, aresCluster2, aresUtilization],
      downloads: [{ label: 'Final Report', url: aresReportPdf }],
    },
  },

  // BI/LL (existing id 5) at priority 200
  {
    id: 5,
    priorityIndex: 200,
    title: 'BI/LL: Bill Splitting and Payment App',
    description:
      'A cross-platform mobile app for splitting restaurant bills in real time. Diners create or join a virtual "table" with a short code, see everyone else at the table as avatars, and walk through an itemized receipt together. Each person picks their items, sees their share update live, and approves the split before payment, all without anyone passing a phone around. Built in React Native and Expo with a Firebase Firestore backend.',
    course: 'CS396, Full Stack Software Engineering',
    image: billTable,
    tag: ['All'],
    // No public gitUrl: the course repo is private (404 to the public).
    detail: {
      overview:
        'BI/LL is a cross-platform mobile application for splitting restaurant bills in real time. Users create or join a "table," see who else is at the table through avatar icons, view the receipt, and approve the split, all without ever passing a phone around. The app is built with React Native and Expo, and Firebase Firestore handles real-time data synchronization between every diner at the table.',
      tech: ['React Native', 'Expo', 'Firebase Firestore', 'TypeScript', 'Figma'],
      highlights: [
        'Firebase Firestore keeps every diner\'s view of the bill in sync in real time.',
        'The table system lets one diner create a session and the rest join with a short code.',
        'Avatar-based presence shows who is currently at the table, with a cartoon fallback for unregistered users.',
        'A receipt modal handles the itemized breakdown and the per-person approval flow.',
        'The UI was prototyped in Figma and is responsive across the full range of phone sizes.',
        'The same React Native codebase ships to both iOS and Android.',
      ],
      images: [billHome, billJoin, billTable, billInvite, billReceipt, billApproval, billPalette],
    },
  },
];

export default projectsData;
